import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  FormControl,
  MenuItem,
  IconButton,
  CircularProgress,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Divider,
  Snackbar,
  Alert,
  Tooltip,
  TablePagination,
} from "@mui/material";
import {
  Upload as UploadIcon,
  FileDownload,
  Visibility,
  VisibilityOff,
  CheckCircle,
  ErrorOutline,
  Close as CloseIcon,
} from "@mui/icons-material";
import Papa from "papaparse";
import axios from "axios";
import { useSelector } from "react-redux";
import { customGetAPI } from "services/utils/CustomAPI";

const sampleCsv = `first_name,last_name,email,phone,company,position,sector,employee_count,source,status,assigned_to,notes,city,street,state,zip,country
John,Doe,john.doe@example.com,+919526314580,Tech Corp,Software Engineer,Technology,250,website,prospect,John,This is a test note,Erode,123 Main St,Tamil nadu,638001,India`;

// Country name (as it might appear in a CSV's "country" column) -> dial code.
// A row's own country column always wins; the dial code picked in the UI is
// only a fallback for rows that don't specify one. Do NOT hardcode a single
// country here — this importer is used by both Indian and UK tenants, and a
// blanket "+91" prefix silently corrupts UK numbers of the same digit length.
const COUNTRY_DIAL_CODES = {
  india: "+91",
  in: "+91",
  "united kingdom": "+44",
  uk: "+44",
  "u.k.": "+44",
  "great britain": "+44",
  gb: "+44",
  england: "+44",
  scotland: "+44",
  wales: "+44",
};

export const DEFAULT_COUNTRY_CODE_OPTIONS = [
  { code: "+91", label: "India (+91)" },
  { code: "+44", label: "United Kingdom (+44)" },
  { code: "", label: "Don't auto-prefix (leave numbers as entered)" },
];

const DEFAULT_COUNTRY_CODE = "+91";

const normalizeImportPhone = (phone, country, fallbackDialCode) => {
  const raw = (phone || "").trim();
  if (!raw) return "";
  if (raw.startsWith("+")) return raw;

  const digits = raw.replace(/\D/g, "");
  if (!digits) return raw;

  const countryKey = (country || "").trim().toLowerCase();
  const dialCode = COUNTRY_DIAL_CODES[countryKey] || fallbackDialCode;
  if (!dialCode) return raw; // no reliable country signal — don't guess

  const dialDigits = dialCode.replace(/\D/g, "");
  // Already has the dial code concatenated without "+" (e.g. "919876543210").
  // Require a full national number after it, otherwise a 10-digit local
  // number that merely starts with the dial digits (e.g. "9198765432") would
  // be mistaken for an already-prefixed one.
  if (digits.startsWith(dialDigits) && digits.length >= dialDigits.length + 10) {
    return `+${digits}`;
  }
  // Strip a leading national trunk "0" (e.g. UK "07911..." -> "7911...")
  const national = digits.startsWith("0") ? digits.slice(1) : digits;
  return `${dialCode}${national}`;
};

// const statusOptions = ["active", "inactive", "prospect", "customer", "lost"];
// const sourceOptions = [
//   "website",
//   "referral",
//   "cold_call",
//   "social_media",
//   "trade_show",
//   "advertisement",
//   "other",
// ];

export default function ContactImport({ open, onClose, onImportSuccess }) {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const [activeStep, setActiveStep] = useState(0);
  const [processingFile, setProcessingFile] = useState(false);
  const [fileName, setFileName] = useState("");
  const [contacts, setContacts] = useState([]); // each has _rowId
  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState({}); // keyed by _rowId -> array of messages
  const [showErrors, setShowErrors] = useState(true);
  const [loadingImport, setLoadingImport] = useState(false);
  const [defaultActivityTypeId, setDefaultActivityTypeId] = useState("");
  const [successCount, setSuccessCount] = useState(0);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [statusOptions, setStatusOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [defaultCountryCode, setDefaultCountryCode] = useState(DEFAULT_COUNTRY_CODE);

  const { domain, token } = useSelector((state) => state.tokenReducer || {});
  const usersRef = useRef([]);
  const fileRef = useRef(null);

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const rowsPerPageOptions = [25, 50, 100];

  useEffect(() => {
    const fetchActivityTypeOptions = async () => {
      try {
        const res = await customGetAPI(`${domain}crm/activity-type/`);
        const types = res.responseDetails?.results || [];

        if (types.length > 0) {
          const noteType = types.find((t) => t.label?.toLowerCase() === "note");
          const defaultTypeId = noteType ? noteType.id : types[0].id;
          setDefaultActivityTypeId(defaultTypeId);
        }
      } catch (err) {
        console.error("Failed to fetch contact status:", err);
      }
    };

    fetchActivityTypeOptions();
  }, [domain]);

  useEffect(() => {
    usersRef.current = users;
  }, [users]);

  const validatePhoneWithAPI = async (phone, domain, token) => {
    try {
      await axios.post(
        `${domain}crm/validate-phone/`,
        { number: phone },
        { headers: { Authorization: `token ${token}` } }
      );
      return { exists: false }; // available
    } catch (err) {
      console.log(err);
      if (err.response?.data?.exists) {
        return {
          exists: true,
          message:
            err.response?.data?.error ||
            `Phone already linked to ${err.response?.data?.contact?.first_name} ${err.response?.data?.contact?.last_name}`,
        };
      }
      return { exists: true, message: "Phone validation failed" };
    }
  };

  const fetchUsers = React.useCallback(async () => {
    try {
      const res = await axios.get(
        `${domain}crm/crm-members/active/?company_id=${requestCompanyId}`,
        {
          headers: { Authorization: `token ${token}` },
        }
      );
      setUsers(res.data || []);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setSnack({
        open: true,
        message: "Failed to load agents. Assigned-to options may be empty.",
        severity: "warning",
      });
    }
  }, [domain, token]);

  useEffect(() => {
    if (open) {
      resetState();
      fetchUsers().catch(console.error);
    }
  }, [open, fetchUsers]);

  useEffect(() => {
    if (!loadingImport) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [loadingImport]);

  useEffect(() => {
    const fetchStatusOptions = async () => {
      try {
        const res = await customGetAPI(`${domain}crm/contact-status/?page_size=100`);
        console.log(res);
        setStatusOptions(res.responseDetails?.results || []);
      } catch (err) {
        console.error("Failed to fetch contact status:", err);
      }
    };

    const fetchSourceOptions = async () => {
      try {
        const res = await customGetAPI(`${domain}crm/contact-source/?page_size=100`);
        setSourceOptions(res.responseDetails?.results || []);
      } catch (err) {
        console.error("Failed to fetch contact sources:", err);
      }
    };

    fetchStatusOptions();
    fetchSourceOptions();
  }, [domain]);

  function resetState() {
    setActiveStep(0);
    setProcessingFile(false);
    setFileName("");
    setContacts([]);
    setErrors({});
    setShowErrors(true);
    setSuccessCount(0);
    setPage(0);
    setRowsPerPage(25);
    setDefaultCountryCode(DEFAULT_COUNTRY_CODE);
  }

  const validateAll = async (data) => {
    const localErrors = validateLocal(data);
    const fullErrors = await validateRemote(data, localErrors);
    return fullErrors;
  };

  const validateLocal = (data) => {
    const all = {};
    const seenPhones = new Map();
    const seenFirstName = new Map();

    data.forEach((row, idx) => {
      const id = row._rowId;
      const rowErrors = [];

      // if (!row.first_name?.trim()) rowErrors.push("First name is required");
      if (row.first_name?.trim()) {
        const normalizedFirstName = row.first_name.trim().toLowerCase();
        if (seenFirstName.has(normalizedFirstName)) {
          const dupRow = seenFirstName.get(normalizedFirstName);
          rowErrors.push(`Duplicate First Name also in row ${dupRow + 1}`);
        } else {
          seenFirstName.set(normalizedFirstName, idx);
        }
      }
      if (!row.email?.trim() && !row.phone?.trim())
        rowErrors.push("Email or phone required");
      if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email))
        rowErrors.push("Invalid email");

      if (row.phone) {
        if (!/^[+\-\d\s()]+$/.test(row.phone)) {
          rowErrors.push("Invalid phone");
        } else {
          const normalized = row.phone.replace(/\D/g, "");
          if (seenPhones.has(normalized)) {
            const dupRow = seenPhones.get(normalized);
            rowErrors.push(`Duplicate phone in row ${dupRow + 1}`);
          } else {
            seenPhones.set(normalized, idx);
          }
        }
      }

      if (row.status && !statusOptions.some((s) => s.id === row.status))
        rowErrors.push("Invalid status");

      if (row.source && !sourceOptions.some((s) => s.id === row.source))
        rowErrors.push("Invalid source");

      if (!row.assigned_to?.trim()) {
        rowErrors.push("Select an agent");
      } else {
        const found = usersRef.current.find(
          (u) =>
            u.username &&
            u.username.toLowerCase() === row.assigned_to.toLowerCase()
        );
        if (!found) rowErrors.push("Agent not found");
      }

      if (rowErrors.length) all[id] = rowErrors;
    });

    return all;
  };

  const validateRemote = async (data, currentErrors) => {
    const all = { ...currentErrors };

    await Promise.all(
      data.map(async (row) => {
        if (row.phone && /^[+\-\d\s()]+$/.test(row.phone)) {
          const res = await validatePhoneWithAPI(row.phone, domain, token);
          if (res.exists) {
            all[row._rowId] = all[row._rowId] || [];
            all[row._rowId].push(res.message);
          }
        }
      })
    );

    return all;
  };

  // Open file manager by button or box click
  const triggerFileSelect = () => {
    fileRef.current?.click();
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      setSnack({
        open: true,
        message: "Please upload a .csv file",
        severity: "error",
      });
      return;
    }
    setFileName(f.name);
    setProcessingFile(true);
    setSnack({ open: true, message: "Processing file...", severity: "info" });

    Papa.parse(f, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const parsed = (results.data || []).map((row, i) => {
          // Map status label -> ID
          const statusObj = statusOptions.find(
            (s) => s.label.toLowerCase() === row.status?.toLowerCase()
          );
          // Map source label -> ID
          const sourceObj = sourceOptions.find(
            (s) => s.label.toLowerCase() === row.source?.toLowerCase()
          );

          return {
            ...row,
            _rowId: i,
            phone: normalizeImportPhone(row.phone, row.country, defaultCountryCode),
            sector: row.sector || row.industry_type || "",
            employee_count: row.employee_count || "",
            status: statusObj?.id || "", // keep blank if not found
            source: sourceObj?.id || "",
          };
        });

        setContacts(parsed);
        const errs = await validateAll(parsed);
        setErrors(errs);
        setProcessingFile(false);
        setSnack({ open: false, message: "", severity: "info" });
        setActiveStep(1);
      },
      error: (err) => {
        console.error("CSV parse error", err);
        setProcessingFile(false);
        setSnack({
          open: true,
          message: "Failed to parse CSV",
          severity: "error",
        });
      },
    });
  };

  const handleFieldChange = async (globalIndex, field, value) => {
    const copy = [...contacts];
    copy[globalIndex] = { ...copy[globalIndex], [field]: value };
    setContacts(copy);

    // revalidate changed row only
    const changed = copy[globalIndex];
    const id = changed._rowId;
    const rowErrs = [];
    if (!changed.first_name?.trim()) rowErrs.push("First name is required");
    if (!changed.email?.trim() && !changed.phone?.trim())
      rowErrs.push("Email or phone required");
    if (changed.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(changed.email))
      rowErrs.push("Invalid email");
    // if (changed.phone && !/^[+\-\d\s()]+$/.test(changed.phone))
    //   rowErrs.push("Invalid phone");
    if (changed.phone && /^[+\-\d\s()]+$/.test(changed.phone)) {
      const normalized = changed.phone.replace(/\D/g, "");
      const duplicates = copy.filter(
        (r, i) => r.phone && r.phone.replace(/\D/g, "") === normalized
      );

      if (duplicates.length > 1) {
        rowErrs.push(
          `Duplicate phone also in row(s): ${duplicates
            .filter((r) => r._rowId !== id)
            .map((r) => r._rowId + 1)
            .join(", ")}`
        );
      }
      validatePhoneWithAPI(changed.phone, domain, token).then((res) => {
        setErrors((prev) => {
          const next = { ...prev };
          const rowErrs = next[id] ? [...next[id]] : [];
          if (res.exists) {
            rowErrs.push(res.message);
            next[id] = [...new Set(rowErrs)];
          } else {
            // remove duplicate-phone errors if fixed
            next[id] = rowErrs.filter(
              (e) => !e.toLowerCase().includes("phone")
            );
            if (!next[id].length) delete next[id];
          }
          return next;
        });
      });
    }
    if (changed.status && !statusOptions.some((s) => s.id === changed.status)) {
      rowErrs.push("Invalid status");
    }

    if (changed.source && !sourceOptions.some((s) => s.id === changed.source)) {
      rowErrs.push("Invalid source");
    }
    if (!changed.assigned_to?.trim()) {
      rowErrs.push("Select an agent");
    } else {
      const found = usersRef.current.find(
        (u) =>
          u.username &&
          u.username.toLowerCase() === changed.assigned_to.toLowerCase()
      );
      if (!found) rowErrs.push("Agent not found");
    }

    setErrors((prev) => {
      const next = { ...prev };
      if (rowErrs.length) next[id] = rowErrs;
      else delete next[id];
      return next;
    });

    const errs = await validateAll(copy);
    setErrors(errs);
  };

  const handleImport = async () => {
    const validRows = contacts.filter((r) => !errors[r._rowId]);
    if (validRows.length === 0) {
      setSnack({
        open: true,
        message: "No valid rows to import",
        severity: "warning",
      });
      return;
    }

    setLoadingImport(true);

    try {
      const rows = validRows.map((c) => {
        const assignedUser = users.find(
          (u) =>
            u.username &&
            c.assigned_to &&
            u.username.toLowerCase() === c.assigned_to.toLowerCase()
        );
        return {
          ...c,
          assigned_to: assignedUser?.id || null,
          source:
            c.source ||
            sourceOptions.find((s) => s.label.toLowerCase() === "website")?.id,
          status:
            c.status ||
            statusOptions.find((s) => s.label.toLowerCase() === "prospect")?.id,
        };
      });

      const response = await axios.post(
        `${domain}crm/contacts/bulk-import/`,
        {
          rows,
          note_activity_type: defaultActivityTypeId || null,
        },
        { headers: { Authorization: `token ${token}` } }
      );

      const results = response.data?.results || [];
      const success = Number(response.data?.success_count || 0);
      const importErrors = results
        .filter((result) => !result.success)
        .map((result) => ({
          rowId: result.rowId,
          message:
            typeof result.message === "string"
              ? result.message
              : JSON.stringify(result.message || "Unknown error"),
        }));

      setErrors((prev) => {
        const next = { ...prev };
        importErrors.forEach((e) => {
          next[e.rowId] = next[e.rowId]
            ? [...next[e.rowId], e.message]
            : [e.message];
        });
        return next;
      });

      setSuccessCount(success);
      setLoadingImport(false);
      setActiveStep(2);
      setSnack({
        open: true,
        message: `${success} contacts imported${importErrors.length ? `, ${importErrors.length} failed` : ""}`,
        severity: importErrors.length ? "warning" : "success",
      });
      if (success > 0) onImportSuccess?.();
      return;
    } catch (err) {
      console.error("Bulk contact import failed:", err);
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        (err?.response?.data
          ? JSON.stringify(err.response.data)
          : "Unknown error");
      setErrors((prev) => {
        const next = { ...prev };
        validRows.forEach((row) => {
          next[row._rowId] = next[row._rowId]
            ? [...next[row._rowId], message]
            : [message];
        });
        return next;
      });
      setLoadingImport(false);
      setSnack({ open: true, message: "Import failed.", severity: "error" });
      return;
    }
  };

  const downloadSample = () => {
    const blob = new Blob([sampleCsv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "contact_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const steps = ["1. Upload File", "2. Validate Data", "3. Import Results"];
  const displayedContacts = contacts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const totalErrorsCount = Object.keys(errors).length;

  // prevent closing on backdrop/esc
  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    onClose?.();
  };

  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: 250,
        overflowY: "auto",
      },
    },
  };

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const firstErrorRow = Math.min(...Object.keys(errors).map(Number));
      const targetPage = Math.floor(firstErrorRow / rowsPerPage);
      setPage(targetPage);
    }
  }, [errors, rowsPerPage]);

  const handleRemoveRow = (rowId) => {
    setContacts((prev) => prev.filter((c) => c._rowId !== rowId));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="xl"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <UploadIcon />
            <Typography variant="h6">Import Contacts</Typography>
          </Box>
          <IconButton onClick={() => onClose?.()} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box mt={3}>
            {activeStep === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 3, minHeight: 220 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Upload CSV File
                    </Typography>

                    {/* Clickable dashed area - now triggers file picker on whole box */}
                    <Box
                      onClick={() => !processingFile && triggerFileSelect()}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        border: "1px dashed",
                        borderColor: "divider",
                        p: 2,
                        borderRadius: 1,
                        cursor: processingFile ? "default" : "pointer",
                      }}
                    >
                      <input
                        type="file"
                        accept=".csv"
                        hidden
                        ref={fileRef}
                        onChange={onFileChange}
                        disabled={processingFile}
                      />
                      <Box display="flex" flexDirection="column" flexGrow={1}>
                        <Typography variant="body2" color="text.secondary">
                          Choose CSV File
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 1 }}>
                          {fileName || "No file chosen"}
                        </Typography>
                      </Box>

                      <Button
                        variant="contained"
                        startIcon={<UploadIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!processingFile) triggerFileSelect();
                        }}
                        disabled={processingFile}
                      >
                        Choose File
                      </Button>
                    </Box>

                    <Box mt={2}>
                      {processingFile ? (
                        <Box display="flex" gap={1} alignItems="center">
                          <CircularProgress size={18} />
                          <Typography variant="body2">
                            Processing file...
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Upload a CSV file with contact information. Required
                          fields: first_name (email or phone).
                        </Typography>
                      )}
                    </Box>

                    <Box mt={2}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 0.5, display: "block" }}
                      >
                        Default country code for phone numbers without one
                        (used only when a row's own "country" column isn't
                        recognized; numbers that already include a country code
                        are preserved)
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={defaultCountryCode}
                          onChange={(e) => setDefaultCountryCode(e.target.value)}
                          disabled={processingFile}
                        >
                          {DEFAULT_COUNTRY_CODE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.code} value={opt.code}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 3, minHeight: 220 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="subtitle1">CSV Template</Typography>
                      <Button
                        variant="outlined"
                        startIcon={<FileDownload />}
                        onClick={downloadSample}
                      >
                        Download
                      </Button>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box
                      sx={{
                        background: "#f9f9f9",
                        borderRadius: 1,
                        p: 1,
                        fontFamily: "monospace",
                        fontSize: "0.85rem",
                        maxHeight: 150,
                        overflow: "auto",
                      }}
                    >
                      {sampleCsv}
                    </Box>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 2, display: "block" }}
                    >
                      Use this template to format your CSV file correctly.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            )}

            {activeStep === 1 && (
              <Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="subtitle1">
                    Validation Results ({contacts.length} contacts)
                  </Typography>
                  <Box display="flex" gap={2} alignItems="center">
                    <Button
                      onClick={() => setShowErrors((p) => !p)}
                      startIcon={
                        showErrors ? <VisibilityOff /> : <Visibility />
                      }
                      color={totalErrorsCount ? "error" : "primary"}
                    >
                      {showErrors
                        ? `Hide Errors (${totalErrorsCount})`
                        : `Show Errors (${totalErrorsCount})`}
                    </Button>

                    <Button
                      variant="contained"
                      onClick={handleImport}
                      disabled={loadingImport || Object.keys(errors).length > 0}
                    >
                      {loadingImport ? (
                        <CircularProgress size={20} />
                      ) : (
                        `Import ${
                          contacts.length - Object.keys(errors).length
                        } Valid Contacts`
                      )}
                    </Button>
                  </Box>
                </Box>

                {totalErrorsCount > 0 && (
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, mb: 2, background: "#fff7e6" }}
                  >
                    <Typography variant="body2" color="text.primary">
                      {totalErrorsCount} row(s) have validation errors. Please
                      fix them before importing. Rows with errors will be
                      skipped.
                    </Typography>
                  </Paper>
                )}

                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>First Name *</TableCell>
                        <TableCell>Last Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Notes</TableCell>
                        <TableCell>Assigned To</TableCell>

                        {/* always reserve Issues column but show details conditionally */}
                        <TableCell>Issues</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayedContacts.map((contact, pageIndex) => {
                        const globalIndex = page * rowsPerPage + pageIndex;
                        const rowErrs = errors[contact._rowId];
                        // always keep shading if error present; show details when showErrors === true
                        const bg = rowErrs ? "#fff2f2" : "inherit";

                        return (
                          <TableRow
                            key={contact._rowId}
                            sx={{ background: bg }}
                          >
                            <TableCell>{globalIndex + 1}</TableCell>
                            <TableCell>
                              <TextField
                                value={contact.first_name || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    globalIndex,
                                    "first_name",
                                    e.target.value
                                  )
                                }
                                size="small"
                                error={
                                  !!rowErrs?.some((r) =>
                                    r.toLowerCase().includes("first name")
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                value={contact.last_name || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    globalIndex,
                                    "last_name",
                                    e.target.value
                                  )
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                value={contact.email || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    globalIndex,
                                    "email",
                                    e.target.value
                                  )
                                }
                                size="small"
                                error={
                                  !!rowErrs?.some((r) =>
                                    r.toLowerCase().includes("email")
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                value={contact.phone || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    globalIndex,
                                    "phone",
                                    e.target.value
                                  )
                                }
                                size="small"
                                error={
                                  !!rowErrs?.some((r) =>
                                    r.toLowerCase().includes("phone")
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <FormControl size="small" fullWidth>
                                <Select
                                  MenuProps={menuProps}
                                  value={contact.source || "all"} // default
                                  onChange={(e) =>
                                    handleFieldChange(
                                      globalIndex,
                                      "source",
                                      e.target.value
                                    )
                                  }
                                >
                                  {sourceOptions.map((s) => (
                                    <MenuItem key={s.id} value={s.id}>
                                      {s.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <FormControl size="small" fullWidth>
                                <Select
                                  MenuProps={menuProps}
                                  value={contact.status || "all"} // default
                                  onChange={(e) =>
                                    handleFieldChange(
                                      globalIndex,
                                      "status",
                                      e.target.value
                                    )
                                  }
                                >
                                  {statusOptions.map((s) => (
                                    <MenuItem key={s.id} value={s.id}>
                                      {s.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <TextField
                                value={contact.notes || ""}
                                onChange={(e) =>
                                  handleFieldChange(
                                    globalIndex,
                                    "notes",
                                    e.target.value
                                  )
                                }
                                size="small"
                                multiline
                                rows={2}
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 180 }}>
                              <FormControl fullWidth size="small">
                                <Select
                                  value={contact.assigned_to || ""}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      globalIndex,
                                      "assigned_to",
                                      e.target.value
                                    )
                                  }
                                  MenuProps={menuProps}
                                  displayEmpty
                                  renderValue={(selected) => {
                                    if (!selected) return "Select Agent";
                                    const user = users.find(
                                      (u) =>
                                        u.username &&
                                        u.username.toLowerCase() ===
                                          selected.toLowerCase()
                                    );
                                    return (
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                      >
                                        <Avatar
                                          src={user?.image_url}
                                          sx={{ width: 24, height: 24 }}
                                        />
                                        {user?.username || selected}
                                      </Box>
                                    );
                                  }}
                                >
                                  {users.map((user) => (
                                    <MenuItem
                                      key={user.id}
                                      value={user.username}
                                    >
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                      >
                                        <Avatar
                                          src={user.image_url}
                                          sx={{ width: 24, height: 24 }}
                                        />
                                        {user.username}
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>

                            <TableCell sx={{ minWidth: 220 }}>
                              {rowErrs ? (
                                // compact view when showErrors === false
                                showErrors ? (
                                  <Box>
                                    {rowErrs.map((r, idx) => (
                                      <Typography
                                        key={idx}
                                        variant="body2"
                                        color="error"
                                      >
                                        • {r}
                                      </Typography>
                                    ))}
                                  </Box>
                                ) : (
                                  <Tooltip title={rowErrs.join(", ")}>
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                    >
                                      <ErrorOutline color="error" />
                                      <Typography variant="body2" color="error">
                                        {rowErrs.length}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                )
                              ) : (
                                <CheckCircle color="success" />
                              )}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ width: 60, minWidth: 60, maxWidth: 60 }}
                            >
                              <Tooltip title="Remove this contact">
                                <IconButton
                                  color="error"
                                  onClick={() =>
                                    handleRemoveRow(contact._rowId)
                                  }
                                  size="small"
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* pagination footer: left=info, right=controls */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={1}
                >
                  <Typography variant="body2" color="text.secondary">
                    Showing {Math.min(contacts.length, page * rowsPerPage + 1)}{" "}
                    - {Math.min(contacts.length, (page + 1) * rowsPerPage)} of{" "}
                    {contacts.length}
                  </Typography>

                  <TablePagination
                    component="div"
                    count={contacts.length}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={rowsPerPageOptions}
                    labelRowsPerPage="" // hide label to keep alignment clean; can show if preferred
                  />
                </Box>
              </Box>
            )}

            {activeStep === 2 && (
              <Box textAlign="center" mt={6}>
                <CheckCircle color="success" sx={{ fontSize: 64 }} />
                <Typography variant="h6" mt={2}>
                  Import Successful
                </Typography>
                <Typography color="text.secondary">
                  {successCount} contacts imported
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => onClose?.()}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity || "info"}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
